import React, { useState, useEffect } from 'react';
import { css } from '@emotion/css';
import ModalLayout from '../structure/ModalLayout';
import FooterBar from '../structure/FooterBar';
import TextField from './TextField';
import TextAreaField from './TextAreaField';
import Button from '../general/Button';
import { color_shades_dark, color_shades_darkest } from '../../constants/colors';
import ApplicationVariablesContainer from '../../hooks/ApplicationVariablesContainer';
import ApplicationVariable from '../../models/applications/ApplicationVariable';
import { getFieldVariationConstantName } from '../../utils/fieldVariations';

const closeIcon = require('../../content/images/close_icon.svg');

interface SaveFieldVariationModalProps {
    fieldTitle: string;
    fieldValue: string;
    isVisible: boolean;
    onClose: () => void;
    applicationId: string;
}

const SaveFieldVariationModal: React.FC<SaveFieldVariationModalProps> = (props) => {
    const variablesContainer = ApplicationVariablesContainer.useContainer();
    const [variationName, setVariationName] = useState('v1');
    const [variationValue, setVariationValue] = useState('');
    const [nameError, setNameError] = useState('');

    useEffect(() => {
        setVariationName('v1');
        setVariationValue(props.fieldValue);
        setNameError('');
    }, [props.isVisible, props.fieldValue]);

    const validateForm = () => {
        if (!variationName.trim()) {
            setNameError('Variation name is required');
            return false;
        }
        setNameError('');
        return true;
    };

    const handleSave = async () => {
        if (validateForm()) {
            const constantName = getFieldVariationConstantName(props.fieldTitle, variationName.trim());
            const variable: ApplicationVariable = {
                name: constantName,
                value: variationValue,
                applicationId: props.applicationId
            };

            const result = await variablesContainer.createApplicationVariable(variable);
            if (result?.resultType === "Ok") {
                props.onClose();
            }
        }
    };

    const fullConstantName = getFieldVariationConstantName(props.fieldTitle, variationName.trim());

    return (
        <ModalLayout
            className={modalStyle}
            isVisible={props.isVisible}
            onClose={props.onClose}
            zIndex={1010}
        >
            <div className={containerStyle}>
                <div className="header-wrapper">
                    <h2>Save Field Variation</h2>
                    <Button
                        className="close-icon"
                        themes={['end-tight', 'icon']}
                        icon={closeIcon}
                        type="button"
                        onClick={props.onClose}
                    />
                </div>

                <div className="form-container">
                    <div className="info-section">
                        <p className="info-label">Field:</p>
                        <p className="info-value">{props.fieldTitle}</p>
                    </div>

                    <TextField
                        name="variationName"
                        value={variationName}
                        placeholder="Enter variation name (e.g., v1, v2, seasonal_2024)"
                        label="Variation Name"
                        onChange={(e) => setVariationName(e.target.value)}
                        useForm={false}
                    />

                    <TextAreaField
                        name="variationValue"
                        value={variationValue}
                        placeholder="Enter the value to save"
                        label="Value"
                        labelClassName={labelNoIndentStyle}
                        onChange={(e) => setVariationValue(e.target.value)}
                        minRows={4}
                        maxRows={15}
                        useForm={false}
                        showResponseVariableTest
                    />
                    {nameError && <div className="error-message">{nameError}</div>}

                    <div className="preview-section">
                        <p className="preview-label">This will be saved as:</p>
                        <p className="preview-value">{fullConstantName}</p>
                    </div>
                </div>

                <FooterBar>
                    <Button
                        themes={['primary', 'wide']}
                        onClick={handleSave}
                        text="Save Variation"
                        type="button"
                    />
                    <Button
                        themes={['white', 'end']}
                        onClick={props.onClose}
                        text="Cancel"
                        type="button"
                    />
                </FooterBar>
            </div>
        </ModalLayout>
    );
};

const modalStyle = css`
    width: 50vw;
    min-width: 500px;
    max-width: 700px;
    height: auto;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    padding-bottom: 100px;

    @media (max-width: 640px) {
        width: calc(100vw - 12px);
        min-width: 0;
        max-width: calc(100vw - 12px);
        height: calc(100vh - 12px);
        height: calc(100dvh - 12px);
        max-height: calc(100vh - 12px);
        max-height: calc(100dvh - 12px);
        border-width: 6px;
        border-radius: 14px;
        padding-bottom: 124px;

        .footer-bar {
            height: 116px;
            display: flex;
            flex-direction: column;
            align-items: stretch;
            justify-content: center;
            gap: 8px;
            padding: 10px 12px;
            box-sizing: border-box;

            button {
                width: 100%;
                min-height: 44px;
                margin: 0;
            }
        }
    }
`;

const containerStyle = css`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;

    .header-wrapper {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 24px 32px 16px 32px;
        border-bottom: solid 1px ${color_shades_dark};
        flex-shrink: 0;

        h2 {
            margin: 0;
            font-size: 24px;
            font-weight: 600;
            color: ${color_shades_darkest};
        }

        .close-icon {
            cursor: pointer;
        }
    }

    .form-container {
        padding: 32px;
        padding-bottom: 16px;
        flex: 1;
        overflow-y: auto;
        min-height: 0;

        .info-section {
            margin-bottom: 24px;

            .info-label {
                font-size: 12px;
                font-weight: 600;
                color: ${color_shades_darkest};
                margin-bottom: 8px;
                text-transform: uppercase;
            }

            .info-value {
                font-size: 14px;
                color: ${color_shades_darkest};
                padding: 12px;
                background-color: #f5f5f5;
                border-radius: 4px;
                margin: 0;

                &.truncated {
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
            }
        }

        .preview-section {
            margin-top: 24px;
            padding: 16px;
            background-color: #f0f8ff;
            border-radius: 4px;
            border: 1px solid #b3d9ff;

            .preview-label {
                font-size: 12px;
                font-weight: 600;
                color: ${color_shades_darkest};
                margin-bottom: 8px;
            }

            .preview-value {
                font-size: 14px;
                font-family: monospace;
                color: ${color_shades_darkest};
                word-break: break-all;
                margin: 0;
            }
        }

        .error-message {
            color: #e74c3c;
            font-size: 12px;
            margin-top: 4px;
            margin-bottom: 16px;
        }
    }

    @media (max-width: 640px) {
        .header-wrapper {
            gap: 12px;
            padding: 14px 16px 12px 16px;

            h2 {
                font-size: 18px;
                line-height: 1.2;
            }

            .close-icon {
                flex: 0 0 auto;
                width: 44px;
                min-width: 44px;
                height: 44px;
                margin: 0;
            }
        }

        .form-container {
            padding: 18px 16px 12px 16px;

            .info-section {
                margin-bottom: 16px;
            }

            .preview-section {
                margin-top: 16px;
                padding: 12px;
            }
        }
    }
`;

const labelNoIndentStyle = css`
    margin-left: 0 !important; /* needs to override */
`;

export default SaveFieldVariationModal;
